<script setup lang="ts">
  import { ref, computed } from 'vue'
  import { CalculatorIcon, ChevronDownIcon } from '@heroicons/vue/24/outline'
  import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
  import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
  import DepreciationCalculator from './calculators/DepreciationCalculator.vue'
  import VehicleDamageCalculator from './calculators/VehicleDamageCalculator.vue'
  import RentalCarCalculator from './calculators/RentalCarCalculator.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import DialogDescription from '@/components/ui/dialog/DialogDescription.vue'

  const { t } = useTranslation({
    en: {
      calculator: 'Calculator',
      vehicleDamage: 'Vehicle Damage Settlement',
      rentalCar: 'Rental Car Compensation',
      depreciation: 'Depreciation'
    },
    de: {
      calculator: 'Rechner',
      vehicleDamage: 'Abrechnung des Fahrzeugschadens',
      rentalCar: 'Mietwagen Nutzungsausfallentschädigung',
      depreciation: 'Wertminderung'
    }
  })

  const isDialogOpen = ref(false)
  const isPopoverOpen = ref(false)
  const selectedItem = ref('')
  const forceRefresh = ref(0)

  const calculatorMap = {
    [t('vehicleDamage')]: VehicleDamageCalculator,
    [t('rentalCar')]: RentalCarCalculator,
    [t('depreciation')]: DepreciationCalculator
  }

  const calculatorItems = Object.keys(calculatorMap)

  function openDialog(item: string) {
    // Force component refresh by incrementing the counter
    forceRefresh.value++
    selectedItem.value = item
    isDialogOpen.value = true
    isPopoverOpen.value = false
  }
</script>

<template>
  <Popover v-model:open="isPopoverOpen">
    <PopoverTrigger>
      <button
        class="inline-flex items-center gap-x-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm
          font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500
          disabled:pointer-events-none disabled:opacity-50"
      >
        <CalculatorIcon class="h-5 w-5" />
        {{ t('calculator') }}
        <ChevronDownIcon class="ml-1 h-4 w-4" />
      </button>
    </PopoverTrigger>
    <PopoverContent
      class="w-80 p-0"
      side="bottom"
      align="end"
    >
      <nav class="space-y-1 p-1">
        <button
          v-for="item in calculatorItems"
          :key="item"
          class="flex w-full items-center rounded-sm px-3 py-2.5 text-sm font-medium hover:bg-gray-100
            focus:bg-gray-100 focus:outline-none"
          @click="openDialog(item)"
        >
          <span class="truncate">{{ item }}</span>
        </button>
      </nav>
    </PopoverContent>
  </Popover>

  <Dialog
    :open="isDialogOpen"
    class="sm:top-[5%]"
    @update:open="isDialogOpen = $event"
  >
    <DialogContent class="flex max-h-[90vh] flex-col overflow-hidden sm:max-w-[600px]">
      <DialogDescription></DialogDescription>
      <DialogHeader>
        <DialogTitle>{{ selectedItem }}</DialogTitle>
      </DialogHeader>
      <div class="flex flex-1 flex-col overflow-hidden">
        <component
          :is="calculatorMap[selectedItem]"
          v-if="selectedItem"
          :key="`${selectedItem}-${forceRefresh}`"
        />
      </div>
    </DialogContent>
  </Dialog>
</template>
